import React from 'react';
import { useBreakpoint, useDesktopScale } from './BreakpointProvider';

interface TwoPaneProps {
  side: React.ReactNode;
  children: React.ReactNode;
  // Desktop: a row above the main pane only (e.g. the page's tabs), so the side pane's top
  // lines up with the main content under it. Below desktop it renders between side and main.
  mainHeader?: React.ReactNode;
  sideWidth?: number;
  gap?: number;
  // 'start' is the right edge in RTL
  sidePosition?: 'start' | 'end';
  // Keep the side pane in view while the main pane scrolls (desktop only)
  stickySide?: boolean;
  stickyTop?: number;
  // Below desktop the panes stack; this places the side pane, or drops it
  sideWhenStacked?: 'before' | 'after' | 'hidden';
}

// A fixed-width side pane next to a fluid main pane on desktop; stacked on phone and tablet.
const TwoPane = ({
  side,
  children,
  mainHeader,
  sideWidth = 300,
  gap = 24,
  sidePosition = 'start',
  stickySide = true,
  stickyTop = 24,
  sideWhenStacked = 'before',
}: TwoPaneProps) => {
  const { isDesktop } = useBreakpoint();
  const scale = useDesktopScale();

  if (!isDesktop) {
    return (
      <>
        {sideWhenStacked === 'before' && side}
        {mainHeader}
        {children}
        {sideWhenStacked === 'after' && side}
      </>
    );
  }

  // Grid columns follow the RTL flow: column 1 is the right edge
  const sideCol = sidePosition === 'start' ? 1 : 2;
  const mainCol = sidePosition === 'start' ? 2 : 1;
  const bodyRow = mainHeader ? 2 : 1;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: sidePosition === 'start' ? `${sideWidth}px minmax(0, 1fr)` : `minmax(0, 1fr) ${sideWidth}px`,
      columnGap: gap,
      alignItems: 'start',
    }}>
      {mainHeader && <div style={{ gridColumn: mainCol, gridRow: 1, minWidth: 0 }}>{mainHeader}</div>}
      <div style={{
        gridColumn: sideCol,
        gridRow: bodyRow,
        minWidth: 0,
        ...(stickySide ? {
          position: 'sticky' as const,
          top: stickyTop,
          // The page area is zoomed, and 100vh inside it scales with it: divide to get one screen
          maxHeight: `calc(100vh / ${scale.content} - ${stickyTop * 2}px)`,
          overflowY: 'auto' as const,
        } : {}),
      }}>
        {side}
      </div>
      <div style={{ gridColumn: mainCol, gridRow: bodyRow, minWidth: 0 }}>{children}</div>
    </div>
  );
};

export default TwoPane;
